import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import config from '../config';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, MenuItem, IconButton, Chip, Typography, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Card, CardContent, Stack, Autocomplete
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Check as CheckIcon,
  Close as CloseIcon, Print as PrintIcon, AssignmentReturn as ReturnIcon,
  FlightTakeoff as LeaveIcon, Info as InfoIcon, Refresh as RefreshIcon, Lock as LockIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useConfirm } from '../hooks/useConfirm';
import { leavesService, Leave, LeaveBalance, CreateLeaveDto, UpdateLeaveDto } from '../services/leaves.service';
import employeesService from '../services/employees.service';
import { branchesService } from '../services/branches.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppSelector } from '../hooks/redux.hooks';
import { Employee } from '../types/employees.types';
import { Branch } from '../types/branches.types';
import AttachmentSection from '../components/common/AttachmentSection';

const LeavesPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const confirm = useConfirm();
  const user = useAppSelector((state) => state.auth.user);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const isSuperAdmin = user?.isSuperAdmin === true;
  const canAcknowledge = isSuperAdmin || permissions?.includes('LEAVE_ACKNOWLEDGE');
  const canApprove = isSuperAdmin || permissions?.includes('LEAVE_APPROVE');
  const canReturn = isSuperAdmin || permissions?.includes('LEAVE_RETURN');

  const userBranchIds = JSON.parse(localStorage.getItem('userBranchIds') || '[]');
  const canEdit = (branchId: number) => userBranchIds.length === 0 || userBranchIds.includes(branchId);

  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [balance, setBalance] = useState<LeaveBalance | null>(null);

  // Filters
  const [filterBranch, setFilterBranch] = useState<number | ''>('');
  const [filterEmployee, setFilterEmployee] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  // Form
  const [form, setForm] = useState<CreateLeaveDto & { leaveId?: number }>({
    employeeId: 0, branchId: 0, leaveType: 'Annual', startDate: '', endDate: '', reason: '', notes: ''
  });

  // Return form
  const [returnDate, setReturnDate] = useState('');
  const [returnNotes, setReturnNotes] = useState('');

  const t = {
    title: isRTL ? 'إدارة الإجازات' : 'Leave Management',
    addNew: isRTL ? 'طلب إجازة' : 'Request Leave',
    employee: isRTL ? 'الموظف' : 'Employee',
    branch: isRTL ? 'الفرع' : 'Branch',
    leaveType: isRTL ? 'نوع الإجازة' : 'Leave Type',
    startDate: isRTL ? 'من تاريخ' : 'Start Date',
    endDate: isRTL ? 'إلى تاريخ' : 'End Date',
    totalDays: isRTL ? 'عدد الأيام' : 'Total Days',
    paidDays: isRTL ? 'أيام مدفوعة' : 'Paid Days',
    unpaidDays: isRTL ? 'بدون راتب' : 'Unpaid Days',
    deduction: isRTL ? 'الخصم' : 'Deduction',
    status: isRTL ? 'الحالة' : 'Status',
    actions: isRTL ? 'الإجراءات' : 'Actions',
    reason: isRTL ? 'السبب' : 'Reason',
    notes: isRTL ? 'ملاحظات' : 'Notes',
    all: isRTL ? 'الكل' : 'All',
    search: isRTL ? 'بحث' : 'Search',
    clear: isRTL ? 'مسح' : 'Clear',
    print: isRTL ? 'طباعة' : 'Print',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    returnToWork: isRTL ? 'عودة للعمل' : 'Return to Work',
    returnDate: isRTL ? 'تاريخ العودة' : 'Return Date',
    returned: isRTL ? 'عاد للعمل' : 'Returned',
    balance: isRTL ? 'الرصيد' : 'Balance',
    remaining: isRTL ? 'متبقي' : 'Remaining',
    used: isRTL ? 'مستخدم' : 'Used',
    entitlement: isRTL ? 'الاستحقاق' : 'Entitlement',
    applied: isRTL ? 'تم التطبيق' : 'Applied',
  };

  const leaveTypes = [
    { value: 'Annual', label: isRTL ? 'سنوية' : 'Annual', labelAr: 'سنوية' },
    { value: 'Sick', label: isRTL ? 'مرضية' : 'Sick', labelAr: 'مرضية' },
    { value: 'Emergency', label: isRTL ? 'طارئة' : 'Emergency', labelAr: 'طارئة' },
    { value: 'Hajj', label: isRTL ? 'حج' : 'Hajj', labelAr: 'حج' },
    { value: 'Umrah', label: isRTL ? 'عمرة' : 'Umrah', labelAr: 'عمرة' },
    { value: 'Unpaid', label: isRTL ? 'بدون راتب' : 'Unpaid', labelAr: 'بدون راتب' },
    { value: 'Maternity', label: isRTL ? 'وضع' : 'Maternity', labelAr: 'وضع' },
    { value: 'Bereavement', label: isRTL ? 'وفاة' : 'Bereavement', labelAr: 'وفاة' },
    { value: 'Iddah', label: isRTL ? 'عدة' : 'Iddah', labelAr: 'عدة' },
  ];

  const getTypeLabel = (type: string) => leaveTypes.find(t => t.value === type)?.label || type;

  const getStatusColor = (status: string): any => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Acknowledged': return 'warning';
      case 'Active': return 'info';
      case 'Completed': return 'default';
      case 'Rejected': return 'error';
      case 'Cancelled': return 'default';
      case 'Pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      Pending: isRTL ? 'معلق' : 'Pending',
      Acknowledged: isRTL ? 'موافقة أولية' : 'Acknowledged',
      Approved: isRTL ? 'معتمد' : 'Approved',
      Active: isRTL ? 'في إجازة' : 'On Leave',
      Completed: isRTL ? 'مكتمل' : 'Completed',
      Rejected: isRTL ? 'مرفوض' : 'Rejected',
      Cancelled: isRTL ? 'ملغي' : 'Cancelled',
    };
    return labels[status] || status;
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [leavesData, empsRes, branchesData, compData] = await Promise.all([
        leavesService.getAll(),
        employeesService.getAll(),
        branchesService.getAll(),
        companyInfoService.getCompanyInfo().catch(() => null),
      ]);
      setLeaves(leavesData);
      setEmployees(empsRes?.data ?? empsRes);
      setBranches(branchesData);
      setCompanyInfo(compData);
    } catch { toast.error(isRTL ? 'خطأ في تحميل البيانات' : 'Error loading data'); }
    finally { setLoading(false); }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const data = await leavesService.getAll(
        filterBranch || undefined, filterEmployee || undefined,
        filterStatus || undefined, filterType || undefined
      );
      setLeaves(data);
    } catch { toast.error(isRTL ? 'خطأ في البحث' : 'Search error'); }
    finally { setLoading(false); }
  };

  const handleAcknowledge = async (leaveId: number) => {
    const ok = await confirm({ message: isRTL ? 'هل تريد الموافقة على هذه الإجازة كمدير عمليات؟' : 'Acknowledge this leave as Operations Manager?' });
    if (!ok) return;
    try {
      await leavesService.acknowledge(leaveId);
      toast.success(isRTL ? 'تمت الموافقة' : 'Acknowledged');
      loadData();
    } catch (e: any) { toast.error(e.response?.data?.message || e.message); }
  };

  const handleApprove = async (leaveId: number, status: string) => {
    const ok = await confirm({ message: isRTL ? `هل تريد ${status === 'Approved' ? 'اعتماد' : 'رفض'} هذه الإجازة؟` : `${status} this leave?` });
    if (!ok) return;
    try {
      await leavesService.approve({ leaveId, status });
      toast.success(isRTL ? 'تم بنجاح' : 'Done');
      loadData();
    } catch (e: any) { toast.error(e.response?.data?.message || e.message); }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: isRTL ? 'هل تريد حذف هذه الإجازة؟' : 'Delete this leave?', severity: 'error' });
    if (!ok) return;
    try {
      await leavesService.delete(id);
      toast.success(isRTL ? 'تم الحذف' : 'Deleted');
      loadData();
    } catch (e: any) { toast.error(e.response?.data?.message || e.message); }
  };

  const handleReturnToWork = async () => {
    if (!selectedLeave || !returnDate) return;
    try {
      await leavesService.returnToWork({ leaveId: selectedLeave.leaveId, actualReturnDate: returnDate, notes: returnNotes });
      toast.success(isRTL ? 'تم تسجيل العودة' : 'Return registered');
      setReturnDialogOpen(false);
      setSelectedLeave(null);
      loadData();
    } catch (e: any) { toast.error(e.response?.data?.message || e.message); }
  };

  const openDialog = (leave?: Leave) => {
    if (leave) {
      setForm({
        leaveId: leave.leaveId, employeeId: leave.employeeId, branchId: leave.branchId,
        leaveType: leave.leaveType, startDate: leave.startDate.split('T')[0],
        endDate: leave.endDate.split('T')[0], reason: leave.reason || '', notes: leave.notes || ''
      });
      setSelectedLeave(leave);
    } else {
      setForm({ employeeId: 0, branchId: 0, leaveType: 'Annual', startDate: '', endDate: '', reason: '', notes: '' });
      setSelectedLeave(null);
    }
    setBalance(null);
    setDialogOpen(true);
  };

  const handleEmployeeChange = async (empId: number) => {
    setForm(prev => ({ ...prev, employeeId: empId }));
    if (empId) {
      try {
        const emp = employees.find(e => e.employeeId === empId);
        if (emp?.branchId) setForm(prev => ({ ...prev, branchId: emp.branchId! }));
        const bal = await leavesService.getBalance(empId);
        setBalance(bal);
      } catch { }
    }
  };

  const handleSave = async () => {
    if (!form.employeeId || !form.branchId || !form.startDate || !form.endDate) {
      toast.error(isRTL ? 'أكمل البيانات المطلوبة' : 'Fill required fields');
      return;
    }
    try {
      if (form.leaveId) {
        await leavesService.update(form.leaveId, form as UpdateLeaveDto);
      } else {
        await leavesService.create(form);
      }
      toast.success(isRTL ? 'تم الحفظ' : 'Saved');
      setDialogOpen(false);
      loadData();
    } catch (e: any) { toast.error(e.response?.data?.message || e.message); }
  };

  const handlePrint = (leave: Leave) => {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    const isReturn = leave.isReturned;
    const docTitle = isReturn ? (isRTL ? 'نموذج عودة للعمل' : 'Return to Work Form') : (isRTL ? 'نموذج طلب إجازة' : 'Leave Request Form');
    const themeColor = '#1565c0';

    w.document.write(`<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><meta charset="UTF-8">
      <title>${docTitle}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 30px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #333; font-size: 13px; }
        .header { text-align: center; border-bottom: 3px solid ${themeColor}; padding-bottom: 15px; margin-bottom: 20px; }
        .header img { max-height: 60px; margin-bottom: 8px; }
        .header h2 { color: ${themeColor}; margin: 5px 0; font-size: 18px; }
        .header .subtitle { font-size: 11px; color: #666; }
        .doc-title { text-align: center; margin-bottom: 20px; }
        .doc-title h3 { color: ${themeColor}; font-size: 20px; border: 2px solid ${themeColor}; display: inline-block; padding: 8px 30px; border-radius: 8px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; margin-bottom: 20px; border: 1px solid #ddd; border-radius: 8px; overflow: hidden; }
        .info-row { display: flex; border-bottom: 1px solid #eee; }
        .info-row:last-child { border-bottom: none; }
        .info-label { font-weight: bold; background: #e3f2fd; padding: 10px 15px; width: 40%; font-size: 12px; color: #333; }
        .info-value { padding: 10px 15px; width: 60%; font-size: 12px; }
        .full-row { grid-column: 1 / -1; }
        .status-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: bold; color: white; }
        .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
        .sig-block { text-align: center; width: 30%; }
        .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 50px; font-weight: bold; font-size: 12px; }
        .print-date { text-align: ${isRTL ? 'left' : 'right'}; font-size: 10px; color: #999; margin-bottom: 10px; }
        @media print { @page { size: A4 portrait; margin: 1.5cm; } }
      </style></head><body>
      <div class="print-date">${isRTL ? 'تاريخ الطباعة:' : 'Print Date:'} ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>

      ${companyInfo ? `
      <div class="header">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
        <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <div class="subtitle">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </div>
      </div>
      ` : ''}

      <div class="doc-title"><h3>${docTitle}</h3></div>

      <div class="info-grid">
        <div class="info-row"><div class="info-label">${isRTL ? 'الموظف' : 'Employee'}</div><div class="info-value">${leave.employeeName} (${leave.employeeCode})</div></div>
        <div class="info-row"><div class="info-label">${isRTL ? 'الفرع' : 'Branch'}</div><div class="info-value">${leave.branchName || '-'}</div></div>
        <div class="info-row"><div class="info-label">${isRTL ? 'نوع الإجازة' : 'Leave Type'}</div><div class="info-value">${getTypeLabel(leave.leaveType)}</div></div>
        <div class="info-row"><div class="info-label">${isRTL ? 'الحالة' : 'Status'}</div><div class="info-value"><span class="status-badge" style="background:${leave.status === 'Approved' ? '#4caf50' : leave.status === 'Rejected' ? '#f44336' : '#ff9800'}">${getStatusLabel(leave.status)}</span></div></div>
        <div class="info-row"><div class="info-label">${isRTL ? 'من تاريخ' : 'From'}</div><div class="info-value">${new Date(leave.startDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</div></div>
        <div class="info-row"><div class="info-label">${isRTL ? 'إلى تاريخ' : 'To'}</div><div class="info-value">${new Date(leave.endDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</div></div>
        <div class="info-row"><div class="info-label">${isRTL ? 'عدد الأيام' : 'Total Days'}</div><div class="info-value"><strong>${leave.totalDays}</strong></div></div>
        <div class="info-row"><div class="info-label">${isRTL ? 'أيام مدفوعة' : 'Paid Days'}</div><div class="info-value">${leave.paidDays}</div></div>
        ${leave.unpaidDays > 0 ? `<div class="info-row"><div class="info-label">${isRTL ? 'أيام بدون راتب' : 'Unpaid Days'}</div><div class="info-value" style="color:#f44336;font-weight:bold">${leave.unpaidDays}</div></div>` : ''}
        ${leave.deductionAmount > 0 ? `<div class="info-row"><div class="info-label">${isRTL ? 'مبلغ الخصم' : 'Deduction'}</div><div class="info-value" style="color:#f44336;font-weight:bold">${leave.deductionAmount.toFixed(3)} KD</div></div>` : ''}
        <div class="info-row full-row"><div class="info-label">${isRTL ? 'السبب' : 'Reason'}</div><div class="info-value">${leave.reason || '-'}</div></div>
        ${leave.notes ? `<div class="info-row full-row"><div class="info-label">${isRTL ? 'ملاحظات' : 'Notes'}</div><div class="info-value">${leave.notes}</div></div>` : ''}
        ${isReturn && leave.actualReturnDate ? `
        <div class="info-row full-row" style="border-top:2px solid ${themeColor}"><div class="info-label" style="background:#c8e6c9">${isRTL ? 'تاريخ العودة الفعلي' : 'Actual Return Date'}</div><div class="info-value" style="font-weight:bold;color:#2e7d32">${new Date(leave.actualReturnDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</div></div>
        ${leave.returnNotes ? `<div class="info-row full-row"><div class="info-label">${isRTL ? 'ملاحظات العودة' : 'Return Notes'}</div><div class="info-value">${leave.returnNotes}</div></div>` : ''}
        ` : ''}
      </div>

      <div class="signatures">
        <div class="sig-block"><div class="sig-line">${isRTL ? 'الموظف' : 'Employee'}</div></div>
        <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير المباشر' : 'Direct Manager'}</div></div>
        <div class="sig-block"><div class="sig-line">${isRTL ? 'مدير الموارد البشرية' : 'HR Manager'}</div></div>
      </div></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{t.title}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={isRTL ? 'تحديث' : 'Refresh'}>
            <IconButton onClick={loadData} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>{t.addNew}</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            options={branches}
            getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
            value={branches.find(b => b.branchId === filterBranch) || null}
            onChange={(_, newValue) => setFilterBranch(newValue?.branchId || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} size="small" />}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <TextField select label={t.leaveType} value={filterType} onChange={e => setFilterType(e.target.value)} size="small" sx={{ minWidth: 130 }}>
            <MenuItem value="">{t.all}</MenuItem>
            {leaveTypes.map(lt => <MenuItem key={lt.value} value={lt.value}>{lt.label}</MenuItem>)}
          </TextField>
          <TextField select label={t.status} value={filterStatus} onChange={e => setFilterStatus(e.target.value)} size="small" sx={{ minWidth: 120 }}>
            <MenuItem value="">{t.all}</MenuItem>
            {['Pending','Acknowledged','Approved','Active','Completed','Rejected','Cancelled'].map(s => <MenuItem key={s} value={s}>{getStatusLabel(s)}</MenuItem>)}
          </TextField>
          <Button variant="contained" onClick={handleSearch}>{t.search}</Button>
          <Button variant="outlined" onClick={() => { setFilterBranch(''); setFilterEmployee(''); setFilterStatus(''); setFilterType(''); loadData(); }}>{t.clear}</Button>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t.employee}</TableCell>
              <TableCell>{t.leaveType}</TableCell>
              <TableCell>{t.startDate}</TableCell>
              <TableCell>{t.endDate}</TableCell>
              <TableCell align="center">{t.totalDays}</TableCell>
              <TableCell align="center">{t.unpaidDays}</TableCell>
              <TableCell align="right">{t.deduction}</TableCell>
              <TableCell>{t.status}</TableCell>
              <TableCell align="center">{t.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaves.map(leave => {
              const isApplied = leave.isApplied;
              const isLocked = isApplied && !isSuperAdmin;
              return (
                <TableRow key={leave.leaveId} sx={isApplied ? { opacity: isSuperAdmin ? 1 : 0.6, bgcolor: 'action.hover' } : undefined}>
                  <TableCell>{isRTL ? leave.employeeNameAr : leave.employeeName}</TableCell>
                  <TableCell>{getTypeLabel(leave.leaveType)}</TableCell>
                  <TableCell>{new Date(leave.startDate).toLocaleDateString('en-GB')}</TableCell>
                  <TableCell>{new Date(leave.endDate).toLocaleDateString('en-GB')}</TableCell>
                  <TableCell align="center">{leave.totalDays}</TableCell>
                  <TableCell align="center">{leave.unpaidDays || '-'}</TableCell>
                  <TableCell align="right">{leave.deductionAmount > 0 ? `${leave.deductionAmount.toFixed(3)} KD` : '-'}</TableCell>
                  <TableCell>
                    {isApplied ? (
                      <Tooltip title={`${isRTL ? 'تم تطبيقها في كشف رواتب' : 'Applied in payroll'}: ${(leave as any).appliedInPayrollNumber || ''}`}>
                        <Chip label={`${t.applied} — ${(leave as any).appliedInPayrollNumber || ''}`} color="info" size="small" icon={<LockIcon />} />
                      </Tooltip>
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {leave.status === 'Acknowledged' && leave.opsManagerAcknowledged ? (
                          <Tooltip title={`${isRTL ? 'وافق عليها' : 'Acknowledged by'}: ${leave.opsManagerName || ''} - ${leave.opsManagerDate ? new Date(leave.opsManagerDate).toLocaleDateString('en-GB') : ''}`}>
                            <Chip label={`${getStatusLabel(leave.status)} - ${leave.opsManagerName || ''}`} color="warning" size="small" />
                          </Tooltip>
                        ) : (
                          <Chip label={getStatusLabel(leave.status)} color={getStatusColor(leave.status)} size="small" />
                        )}
                        {leave.isReturned && <Chip label={t.returned} size="small" variant="outlined" color="success" />}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t.print}>
                      <IconButton size="small" onClick={() => handlePrint(leave)}><PrintIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {!isLocked && (
                      <>
                        {/* Ops Manager acknowledge — only for Pending */}
                        {leave.status === 'Pending' && !leave.opsManagerAcknowledged && canAcknowledge && !canApprove && (
                          <Tooltip title={isRTL ? 'موافقة مدير العمليات' : 'Ops Manager Acknowledge'}>
                            <IconButton size="small" color="warning" onClick={() => handleAcknowledge(leave.leaveId)}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* HR/SuperAdmin final approve — only with LEAVE_APPROVE */}
                        {(leave.status === 'Pending' || leave.status === 'Acknowledged') && canApprove && (
                          <>
                            <Tooltip title={isRTL ? 'اعتماد' : 'Approve'}>
                              <IconButton size="small" color="success" onClick={() => handleApprove(leave.leaveId, 'Approved')}><CheckIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title={isRTL ? 'رفض' : 'Reject'}>
                              <IconButton size="small" color="error" onClick={() => handleApprove(leave.leaveId, 'Rejected')}><CloseIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </>
                        )}
                        {(leave.status === 'Approved' || leave.status === 'Active') && !leave.isReturned && canReturn && (
                          <Tooltip title={t.returnToWork}>
                            <IconButton size="small" color="primary" onClick={() => { setSelectedLeave(leave); setReturnDate(''); setReturnNotes(''); setReturnDialogOpen(true); }}>
                              <ReturnIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(leave.status === 'Pending' || leave.status === 'Acknowledged') && canEdit(leave.branchId) && (
                          <>
                            <IconButton size="small" onClick={() => openDialog(leave)}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(leave.leaveId)}><DeleteIcon fontSize="small" /></IconButton>
                          </>
                        )}
                      </>
                    )}
                    {isSuperAdmin && (leave.status === 'Approved' || leave.status === 'Active' || leave.status === 'Completed' || isApplied) && (
                      <>
                        <IconButton size="small" onClick={() => openDialog(leave)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(leave.leaveId)}><DeleteIcon fontSize="small" /></IconButton>
                      </>
                    )}
                    <AttachmentSection documentType="Leave" documentId={leave.leaveId} isRTL={isRTL} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.leaveId ? (isRTL ? 'تعديل إجازة' : 'Edit Leave') : (isRTL ? 'طلب إجازة جديدة' : 'New Leave Request')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              options={employees}
              getOptionLabel={(option) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
              value={employees.find(e => e.employeeId === form.employeeId) || null}
              onChange={(_, newValue) => handleEmployeeChange(newValue?.employeeId || 0)}
              renderInput={(params) => <TextField {...params} label={t.employee} />}
              fullWidth
            />

            {balance && (
              <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Stack direction="row" spacing={3} justifyContent="center">
                    <Box textAlign="center">
                      <Typography variant="caption" color="text.secondary">{t.entitlement}</Typography>
                      <Typography fontWeight={700}>{balance.totalEntitlement}</Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="caption" color="text.secondary">{t.used}</Typography>
                      <Typography fontWeight={700} color="error.main">{balance.usedDays}</Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="caption" color="text.secondary">{t.remaining}</Typography>
                      <Typography fontWeight={700} color="success.main">{balance.remainingDays}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Autocomplete
              options={branches}
              getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
              value={branches.find(b => b.branchId === form.branchId) || null}
              onChange={(_, newValue) => setForm(prev => ({ ...prev, branchId: newValue?.branchId || 0 }))}
              renderInput={(params) => <TextField {...params} label={t.branch} />}
              fullWidth
            />

            <TextField select label={t.leaveType} value={form.leaveType} onChange={e => setForm(prev => ({ ...prev, leaveType: e.target.value }))} fullWidth>
              {leaveTypes.map(lt => <MenuItem key={lt.value} value={lt.value}>{lt.label}</MenuItem>)}
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.startDate}
                value={form.startDate ? new Date(form.startDate) : null}
                onChange={d => setForm(prev => ({ ...prev, startDate: d ? d.toISOString().split('T')[0] : '' }))}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label={t.endDate}
                value={form.endDate ? new Date(form.endDate) : null}
                onChange={d => setForm(prev => ({ ...prev, endDate: d ? d.toISOString().split('T')[0] : '' }))}
                format="dd/MM/yyyy"
                minDate={form.startDate ? new Date(form.startDate) : undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>

            <TextField label={t.reason} value={form.reason} onChange={e => setForm(prev => ({ ...prev, reason: e.target.value }))} fullWidth multiline rows={2} />
            <TextField label={t.notes} value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleSave}>{t.save}</Button>
        </DialogActions>
      </Dialog>

      {/* Return to Work Dialog */}
      <Dialog open={returnDialogOpen} onClose={() => setReturnDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t.returnToWork}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {selectedLeave && (
              <Typography variant="body2" color="text.secondary">
                {isRTL ? `إجازة ${selectedLeave.employeeName} من ${new Date(selectedLeave.startDate).toLocaleDateString('en-GB')} إلى ${new Date(selectedLeave.endDate).toLocaleDateString('en-GB')}`
                  : `${selectedLeave.employeeName}'s leave from ${new Date(selectedLeave.startDate).toLocaleDateString('en-GB')} to ${new Date(selectedLeave.endDate).toLocaleDateString('en-GB')}`}
              </Typography>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.returnDate}
                value={returnDate ? new Date(returnDate) : null}
                onChange={d => setReturnDate(d ? d.toISOString().split('T')[0] : '')}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            <TextField label={t.notes} value={returnNotes} onChange={e => setReturnNotes(e.target.value)} fullWidth multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialogOpen(false)}>{t.cancel}</Button>
          <Button variant="contained" color="primary" onClick={handleReturnToWork} startIcon={<ReturnIcon />}>{t.returnToWork}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeavesPage;
